import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Currency } from "@/lib/currency";
import { getLanguageFromPath, stripLangPrefix } from "@/lib/i18n";

const STORAGE_KEY = "preferred_currency";

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);
const isCurrency = (value: unknown): value is Currency => value === "pln" || value === "eur";

function readShoppingCurrency(fallback: Currency): Currency {
  try {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (isCurrency(stored)) return stored;
    }
  } catch {
    // Storage can be unavailable in private browsing; in-memory selection still works.
  }
  return fallback;
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const baseCurrency = getLanguageFromPath(pathname) === "pl" ? "pln" : "eur";
  // Stripe also returns to /en/checkout/success, an existing unprefixed-language alias.
  const shoppingRoute = /^\/(?:en\/)?(account|cart|checkout|register|oauth)(?:\/|$)/.test(stripLangPrefix(pathname));
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [shoppingCurrency, setShoppingCurrency] = useState<Currency>(() =>
    shoppingRoute ? readShoppingCurrency(baseCurrency) : baseCurrency,
  );
  // Public URLs render the same price during SSG and hydration. A deliberate
  // selection lasts for this SPA session; a public reload returns to the URL default.
  // Non-prerendered account/checkout routes can restore the last shopping currency.
  const currency = selectedCurrency ?? (shoppingRoute ? shoppingCurrency : baseCurrency);

  useEffect(() => {
    setShoppingCurrency(currency);
    try {
      window.localStorage.setItem(STORAGE_KEY, currency);
    } catch {
      // A blocked storage write must not interrupt browsing or checkout.
    }
  }, [currency]);

  const setCurrency = (nextCurrency: Currency) => {
    if (isCurrency(nextCurrency)) setSelectedCurrency(nextCurrency);
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
};

/** Currency for translated price tokens, including the provider-free route error boundary. */
export const useCurrencyOrDefault = (): Currency => useContext(CurrencyContext)?.currency ?? "eur";
